import React from "react";
import type { ReactionGroup } from "../../services/rtkApi/agentApi";
import { ReactionPicker } from "./ReactionPicker";

interface ReactionDetailsProps {
  reactions: ReactionGroup[];
  /** toggle the current user's reaction with this emoji on/off */
  onToggle: (emoji: string) => void;
  onClose: () => void;
  align?: "start" | "end";
  /** current user's display name — used to label their own reaction as "you" */
  me: string;
}

const BOT_LABEL = "sitebot";

// readable reactor list for one emoji group: "you" first, then other humans, then the bot.
function reactorNames(g: ReactionGroup, me: string): string[] {
  const names: string[] = [];
  if (g.mine || g.reactors.includes(me)) names.push("you");
  for (const r of g.reactors) if (r !== me) names.push(r);
  if (g.agentReacted) names.push(BOT_LABEL);
  return names;
}

/**
 * Popover shown when a reaction chip (or the add button) is tapped. Lists WHO reacted with each
 * emoji — including sitebot — and lets the user toggle their own reaction explicitly (via the
 * quick-pick palette or by tapping a row). Because toggling lives here, a bare chip tap can
 * never silently add/remove a reaction by accident.
 */
export function ReactionDetails({
  reactions,
  onToggle,
  onClose,
  align = "start",
  me,
}: ReactionDetailsProps) {
  const mine = new Set(reactions.filter((r) => r.mine).map((r) => r.emoji));
  const groups = [...reactions].sort(
    (a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji),
  );

  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-label="reactions"
        className={`absolute bottom-full z-20 mb-1 w-60 max-w-[80vw] rounded-sm border-2 border-ink bg-white p-2 ${
          align === "end" ? "right-0" : "left-0"
        }`}
        style={{ boxShadow: "3px 3px 0px rgba(0,0,0,0.18)" }}
      >
        {/* quick add / toggle */}
        <ReactionPicker onPick={onToggle} mine={mine} />

        {groups.length > 0 && <div className="my-1.5 border-t border-ink/10" />}

        {/* who reacted */}
        {groups.length > 0 && (
          <div className="flex max-h-44 flex-col gap-0.5 overflow-y-auto">
            {groups.map((g) => (
              <button
                key={g.emoji}
                type="button"
                onClick={() => onToggle(g.emoji)}
                title={g.mine ? "tap to remove your reaction" : "tap to react"}
                className={`flex items-start gap-2 rounded-sm px-1.5 py-1 text-left transition-colors ${
                  g.mine ? "bg-sky/15" : "hover:bg-gray-50"
                }`}
              >
                <span className="shrink-0 text-base leading-none">{g.emoji}</span>
                <span className="min-w-0 break-words text-[11px] leading-tight text-gray-600">
                  {reactorNames(g, me).join(", ")}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
